import { Box, Stack, Typography, useTheme } from '@mui/material';
import { Dayjs } from 'dayjs';
import React from 'react';

import {
    confirmationAuthTextSx,
    confirmationInfoTextSx,
    confirmationModalContentSx,
    confirmationTableSx,
    confirmationTransactionTextSx,
    getConfirmationHighlightSx,
} from '../../pages/dashboard/DashboardStyles';
import { formatCurrency, formatInET } from '../../utils/helper';
import { CommonModal } from '../modal/modal';
import CommonTable from '../tableComponent/commonTableComponent';
import { colors } from '../theme/theme';

import type { BankAccountOption } from '../../hooks/make-payment/useContractPaymentData';
import type { PaymentRow } from '../../hooks/make-payment/usePaymentRows';
import type { ContractApiResponse } from '../../services/contract-services';

interface PaymentConfirmationModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    paymentRows: PaymentRow[];
    totalAmount: number;
    userDetails: Record<string, unknown> | null | undefined;
    bankAccountOptions: BankAccountOption[];
    getContractById: (contractId: string) => ContractApiResponse | null;
    getEffectivePaymentAmount: (row: PaymentRow, contract: ContractApiResponse | null | undefined) => number;
}

/** The "Confirmation" modal shown before submitting a bulk bank-account payment. */
export const PaymentConfirmationModal: React.FC<PaymentConfirmationModalProps> = ({
    open,
    onClose,
    onConfirm,
    paymentRows,
    totalAmount,
    userDetails,
    bankAccountOptions,
    getContractById,
    getEffectivePaymentAmount,
}) => {
    const theme = useTheme();

    return (
        <CommonModal
            modalOpen={open}
            onCloseModal={onClose}
            DialogMainTitle="Confirmation"
            primaryBtnName="Close"
            secondaryBtnName="Confirm"
            primaryBtnClick={onClose}
            secondaryBtnClick={onConfirm}
            ModalMaxWidth={740}
        >
            <Box sx={confirmationModalContentSx}>
                {(() => {
                    const getPaymentDate = (paymentDate: Dayjs | null) => {
                        if (paymentDate) {
                            return paymentDate.format('MM/DD/YYYY');
                        }
                        return formatInET(new Date());
                    };

                    const userName = (userDetails?.Name as string) || (userDetails?.FirstName as string) || 'User';
                    const totalAmountFormatted = formatCurrency(totalAmount);

                    return (
                        <Stack spacing={3} textAlign={'center'}>
                            {/* Authorization Message */}
                            <Typography variant="body1" sx={confirmationAuthTextSx}>
                                <Typography variant="h6" component={'span'} sx={getConfirmationHighlightSx(theme)}>
                                    Please read:
                                </Typography>
                                By clicking the button to submit this payment, you,{' '}
                                <Typography variant="h6" component={'span'} sx={getConfirmationHighlightSx(theme)}>
                                    {userName},
                                </Typography>
                                authorize a debit of{' '}
                                <Typography variant="h6" component={'span'} sx={getConfirmationHighlightSx(theme)}>
                                    {totalAmountFormatted}
                                </Typography>
                                from below mentioned accounts.The withdrawal from your account will generally occur
                                within two business days of this date, subject to your bank&apos;s processing schedule.
                            </Typography>

                            {/* Payment Details Table */}
                            {(() => {
                                const paymentTableRows = [
                                    ...paymentRows.map(row => {
                                        const contract =
                                            getContractById(row.contractId) || getContractById(row.dbContractId);
                                        const effectiveAmount = getEffectivePaymentAmount(row, contract ?? undefined);
                                        const selectedAccount = bankAccountOptions.find(
                                            acc => acc.id === row.bankAccount,
                                        );
                                        const accountNumber = selectedAccount?.name
                                            ? selectedAccount.name.match(/\*{4}(\d{4})/)?.[0] || '****'
                                            : '****';
                                        return {
                                            accountNo: accountNumber,
                                            paymentDate: getPaymentDate(row?.paymentDate ?? null),
                                            paymentAmount: formatCurrency(effectiveAmount),
                                        };
                                    }),
                                    {
                                        accountNo: 'Total',
                                        paymentDate: '',
                                        paymentAmount: totalAmountFormatted,
                                        isTotalRow: true,
                                    },
                                ];
                                return (
                                    <CommonTable
                                        columns={[
                                            { id: 'accountNo', label: 'Account No' },
                                            { id: 'paymentDate', label: 'Payment Date' },
                                            { id: 'paymentAmount', label: 'Payment Amount', align: 'right' },
                                        ]}
                                        rows={paymentTableRows}
                                        isPagination={false}
                                        getRowSx={(row: Record<string, unknown>) =>
                                            (row as { isTotalRow?: boolean }).isTotalRow
                                                ? { fontWeight: 600, color: colors.primaryLight[100] }
                                                : { color: colors.fullBlack }
                                        }
                                        sx={confirmationTableSx}
                                        className="tableWrapper"
                                    />
                                );
                            })()}

                            {/* Additional Information */}
                            <Typography variant="body1" sx={confirmationInfoTextSx(theme)}>
                                Application of payment is subject to receipt of good funds. Credit for this payment may
                                not be applied until actual receipt of funds. If this payment is time-sensitive, contact
                                us to review payment options.
                            </Typography>
                            <Typography variant="body1" sx={confirmationInfoTextSx(theme)}>
                                Scheduled transactions may be cancelled prior to the payment date. Payments will be
                                applied in accordance with the terms of the underlying agreement(s).
                            </Typography>
                            <Typography variant="body2" sx={confirmationTransactionTextSx} color="textSecondary">
                                This request will be executed as {paymentRows?.length}{' '}
                                {paymentRows?.length === 1 ? 'separate transaction' : 'separate transactions'}.
                            </Typography>
                        </Stack>
                    );
                })()}
            </Box>
        </CommonModal>
    );
};
